import React from "react";
import { Button, Chip, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { orderBy, groupBy } from "lodash";

import { Card } from "./game/shared/Card";
import { Size, TeamScore } from "../types/types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useSelector } from "react-redux";
import {
  selectCurrentMatchId,
  selectGameTeamScores,
  selectViewingGameDetailsId,
  selectCurrentMatchGames,
  selectGameOutcome,
} from "../store/selectors";
import { BIDS, RUFER_OPTIONS } from "../constants";

export const GameDetailsView: React.FC = () => {
  const { setViewingGameDetailsId } = useCurrentUser();
  const matchId = useSelector(selectCurrentMatchId);
  const gameId = useSelector(selectViewingGameDetailsId);
  const games = useSelector(selectCurrentMatchGames);
  
  const game = games?.find((g) => g.id === gameId);
  const teamScores = useSelector(
    selectGameTeamScores(matchId ?? "", gameId ?? "")
  );
  const gameOutcome = useSelector(
    selectGameOutcome(matchId ?? "", gameId ?? "")
  );

  const handleBack = () => {
    setViewingGameDetailsId(null);
  };

  if (!matchId || !gameId || !game || teamScores === undefined || !gameOutcome) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 flex items-center justify-center">
        <div className="text-emerald-200 animate-pulse">Loading details...</div>
      </div>
    );
  }

  const bidDetails =
    BIDS.find((b) => b.slug === gameOutcome.bid.slug) ??
    RUFER_OPTIONS.find((b) => b.slug === gameOutcome.bid.slug);
  const bidName = bidDetails?.name ?? gameOutcome.bid.slug;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <IconButton
            onClick={handleBack}
            sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Hand {game.gameIndex + 1}
            </h1>
            <p className="text-emerald-300 mt-1">
              Contract: {bidName}
            </p>
          </div>
        </div>

        {/* Team Scores */}
        <div className="flex flex-col gap-6 mb-8">
          {teamScores.map((ts: TeamScore) => {
            const groupedTricks = Object.values(
              groupBy(orderBy(ts.trickCards, "trickIndex"), "trickIndex")
            );
            return (
              <div
                className={`bg-white/95 rounded-xl p-6 shadow-lg ${
                  ts.winner ? "ring-4 ring-emerald-400" : ""
                }`}
                key={ts.players.map((p) => p.name).join(",")}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-emerald-900">
                      {ts.declarers ? "Declarers" : "Defenders"}
                    </h2>
                    <div className="flex gap-2">
                      {ts.players.map((p) => (
                        <Chip
                          key={p.name}
                          label={p.name}
                          sx={{ backgroundColor: "#d1fae5", color: "#065f46" }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Card Points</div>
                      <div className="text-2xl font-bold text-emerald-700">
                        {ts.cardPoints}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Match Points</div>
                      <div className={`text-2xl font-bold ${
                        ts.matchPoints > 0 ? "text-emerald-700" : ts.matchPoints < 0 ? "text-red-600" : "text-gray-600"
                      }`}>
                        {ts.matchPoints > 0 ? "+" : ""}{ts.matchPoints}
                      </div>
                    </div>
                    {ts.winner ? (
                      <Chip
                        label="Winner!"
                        color="success"
                        sx={{ fontWeight: "bold", fontSize: "1rem", py: 2 }}
                      />
                    ) : (
                      <Chip label="Loser" variant="outlined" />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-semibold text-gray-500 mb-2">
                      Tricks Won ({groupedTricks.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {groupedTricks.map((trickCards) => (
                        <div
                          className="flex -space-x-6 bg-gray-100 rounded-lg p-1"
                          key={trickCards[0].trickIndex}
                        >
                          {trickCards.map((tc) => (
                            <Card slug={tc.slug} size={Size.XS} key={tc.slug} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    {ts.talonCards && ts.talonCards.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 mb-2">
                          Talon
                        </h3>
                        <div className="flex -space-x-4">
                          {ts.talonCards.map((slug) => (
                            <Card slug={slug} size={Size.XS} key={slug} />
                          ))}                        </div>
                      </div>
                    )}

                    {ts.putdownCards && ts.putdownCards.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 mb-2">
                          Putdowns
                        </h3>
                        <div className="flex -space-x-4">
                          {ts.putdownCards.map((slug) => (
                            <Card slug={slug} size={Size.XS} key={slug} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Back Button */}
        <div className="flex justify-center">
          <Button
            onClick={handleBack}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#059669",
              "&:hover": { backgroundColor: "#047857" },
              px: 6,
              py: 2,
              fontSize: "1.1rem",
            }}
          >
            Back to Scores
          </Button>
        </div>
      </div>
    </div>
  );
};
